<template>
  <div v-show="isOpen">
    <div ref="pageOverlay" class="page-overlay" :style="viewportStyle">
      <div class="overlay-lightbox" :class="lightboxClasses" ref="overlay">
        <div class="overlay-content" :class="overlayContentClasses">
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import EventBus from '@/event-bus/event-bus';

const OFFSET_SETTLE_MS = 120;

export default {
  name: 'PageOverlay',
  props: {
    isOpen: Boolean,
    popupAlignment: {
      type: String,
      default: 'start',
      validator: val => ['start', 'center', 'full-screen', 'fit-height', 'menu'].includes(val),
    },
    // A fixed 100%-tall overlay follows the layout viewport, which iOS keeps
    // full-size behind the keyboard; content sized from the visual viewport
    // then jumps until the browser scrolls it into place. Tracking the visual
    // viewport's offset keeps the overlay on the visible area, the part of it
    // the keyboard covers is exposed as --keyboard-inset for content to lift
    // itself above, and
    // the browser's scroll-to-reveal-the-input is undone while a field inside
    // the overlay has focus, since the overlay places the field itself.
    trackVisualViewport: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      viewportStyle: null,
      keyboardOpen: false,
    };
  },
  watch: {
    isOpen: 'syncViewportTracking',
    trackVisualViewport: 'syncViewportTracking',
  },
  mounted() {
    EventBus.$on('reset-scroll', this.scrollTopCallback);
    this.syncViewportTracking();
  },
  beforeDestroy() {
    EventBus.$off('reset-scroll', this.scrollTopCallback);
    this.stopViewportTracking();
  },
  computed: {
    isFullscreenLayout() {
      return this.popupAlignment === 'full-screen' || this.popupAlignment === 'menu';
    },
    lightboxClasses() {
      return {
        center: this.popupAlignment === 'center',
        'full-screen': this.isFullscreenLayout,
        menu: this.popupAlignment === 'menu',
      };
    },
    overlayContentClasses() {
      return {
        'full-screen': this.isFullscreenLayout,
        'fit-height': this.popupAlignment === 'fit-height',
        menu: this.popupAlignment === 'menu',
      };
    },
  },
  methods: {
    scrollTopCallback() {
      this.$refs.overlay.scrollTop = 0;
    },
    syncViewportTracking() {
      if (this.isOpen && this.trackVisualViewport) {
        this.startViewportTracking();
      } else {
        this.stopViewportTracking();
      }
    },
    startViewportTracking() {
      const viewport = window.visualViewport;
      if (!viewport || this.trackingViewport) { return; }
      this.trackingViewport = true;
      viewport.addEventListener('resize', this.updateViewportStyle);
      viewport.addEventListener('scroll', this.updateViewportStyle);
      document.addEventListener('focusin', this.onOverlayFocusIn);
      document.addEventListener('focusout', this.onOverlayFocusOut);
      this.updateViewportStyle();
    },
    stopViewportTracking() {
      const viewport = window.visualViewport;
      if (viewport && this.trackingViewport) {
        viewport.removeEventListener('resize', this.updateViewportStyle);
        viewport.removeEventListener('scroll', this.updateViewportStyle);
        document.removeEventListener('focusin', this.onOverlayFocusIn);
        document.removeEventListener('focusout', this.onOverlayFocusOut);
      }
      clearTimeout(this.offsetSettleTimer);
      clearTimeout(this.focusOutTimer);
      this.trackingViewport = false;
      this.keyboardOpen = false;
      this.settledOffsetTop = 0;
      this.viewportStyle = null;
    },
    updateViewportStyle() {
      if (!this.trackingViewport) { return; }
      // The transform/inset only mean anything while a field inside the overlay
      // holds the keyboard open — a viewport change with no keyboard (e.g. a
      // desktop or tablet pinch-zoom) must not shift the overlay.
      if (!this.keyboardOpen) {
        clearTimeout(this.offsetSettleTimer);
        this.settledOffsetTop = 0;
        this.viewportStyle = null;
        return;
      }
      this.resetFocusScroll();
      const viewport = window.visualViewport;
      // While the keyboard animates, iOS reports a reveal-scroll offset a few
      // frames before (or without ever) moving the viewport; following it at
      // once shoves the overlay down. A zero offset applies immediately, a
      // non-zero one only once it has held for a moment.
      clearTimeout(this.offsetSettleTimer);
      if (viewport.offsetTop === 0) {
        this.settledOffsetTop = 0;
      } else if (viewport.offsetTop !== this.settledOffsetTop) {
        this.offsetSettleTimer = setTimeout(() => {
          this.settledOffsetTop = window.visualViewport.offsetTop;
          this.applyViewportStyle();
        }, OFFSET_SETTLE_MS);
      }
      this.applyViewportStyle();
    },
    applyViewportStyle() {
      const viewport = window.visualViewport;
      const offsetTop = this.settledOffsetTop || 0;
      // The overlay keeps its full height so its content paints all the way
      // down, including the band iOS leaves between the reported viewport and
      // the real keyboard top; the inset says how much of the bottom is covered.
      const overlay = this.$refs.pageOverlay;
      const overlayHeight = overlay ? overlay.getBoundingClientRect().height : viewport.height;
      // The transform below already moves the overlay down by offsetTop, so the
      // covered band is just the height difference — subtracting offsetTop again
      // would leave the content that far behind the keyboard.
      const inset = Math.max(0, Math.round(overlayHeight - viewport.height));
      this.viewportStyle = {
        '--keyboard-inset': `${inset}px`,
        transform: `translateY(${offsetTop}px)`,
      };
    },
    // The keyboard is detected by focus, not viewport math: iOS Chrome shrinks
    // innerHeight itself, so height comparisons read "no keyboard". A coarse
    // pointer keeps desktop hardware keyboards out of this.
    isKeyboardFocus(element) {
      return Boolean(element)
        && Boolean(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
        && this.$el.contains(element)
        && ['INPUT', 'TEXTAREA'].includes(element.tagName);
    },
    onOverlayFocusIn(event) {
      if (!this.isKeyboardFocus(event.target)) { return; }
      if (!this.keyboardOpen) {
        // Remembered so undoing the browser's reveal-scroll restores the page
        // behind the overlay instead of sending it to the top.
        this.savedScrollY = window.scrollY;
        this.savedScrollTop = document.scrollingElement ? document.scrollingElement.scrollTop : 0;
      }
      this.keyboardOpen = true;
      // The keyboard opens over ~300ms and the browser reveals the input in
      // several steps along the way; keep correcting through the animation.
      let ticks = 0;
      const step = () => {
        if (!this.keyboardOpen) { return; }
        this.updateViewportStyle();
        ticks += 1;
        if (ticks < 40) { requestAnimationFrame(step); }
      };
      requestAnimationFrame(step);
    },
    onOverlayFocusOut() {
      // activeElement still names the old field during focusout. The handle is
      // kept so stopViewportTracking can cancel a pending check — otherwise it
      // would re-apply a stale style after cleanup.
      clearTimeout(this.focusOutTimer);
      this.focusOutTimer = setTimeout(() => {
        this.keyboardOpen = this.isKeyboardFocus(document.activeElement);
        this.updateViewportStyle();
      }, 0);
    },
    resetFocusScroll() {
      const targetY = this.savedScrollY || 0;
      if (window.scrollY !== targetY) { window.scrollTo(0, targetY); }
      const scroller = document.scrollingElement;
      const targetTop = this.savedScrollTop || 0;
      if (scroller && scroller.scrollTop !== targetTop) { scroller.scrollTop = targetTop; }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';
@import '~@riseupil/base-ui/src/scss/border-radius';
@import '../scss/mixins';

.page-overlay {
  width: 100%;
  height: 100%;
  position: fixed; // TODO add somthing from elsa that disable scroll from background
  display: flex;
  justify-content: center;

  .overlay-lightbox {
    @include responsive-padding;
    @include responsive-padding-top-bottom;
    @include responsive-width-and-margin;
    height: 100%;
    background-color: rgba(43, 50, 59, 0.84);
    font-family: SimplerPro;
    top: 0;
    right: 0;
    text-align: center;
    overflow-y: auto;
    scroll-behavior: smooth;
    display: flex;
    align-items: flex-start;

    &.center {
      align-items: center;
    }

    &.full-screen {
      padding: 0;
    }

    &.menu {
      background: initial;
    }

    .overlay-content {
      width: 100%;
      display: flex;
      flex-direction: column;
      flex: 1;
      background: $riseup_white;
      border-radius: $popup-border-radius;

      &.fit-height {
        display: flex;
        flex-direction: column;
        max-height: 100%;
        overflow: scroll;
      }

      &.full-screen {
        height: 100%;
        border-radius: 0;
      }

      &.menu {
        background: initial;
      }
    }
  }
}

@media (min-width: 460px) {
  .page-overlay {
    .overlay-lightbox {
      height: calc(100vh - 100vh*0.1 - 35px - 20px);
    }
  }
}
</style>
